import * as moment from 'moment-timezone';

/**
 * Fila de las tablas del flujo de pipas (Solicitudes del solicitante y panel admin): un pedido de /purchases con el
 * cambio en vuelo mezclado encima (in_flight_change) o una fila de historial. Solo los campos que leen estas reglas;
 * el resto de la fila sigue siendo dinámico.
 */
export interface PipesOrderRow {
  status?: string;
  /** Fecha requerida por la estación (ISO). */
  order_delivery_date?: string | null;
  /** Fecha de entrega aceptada por MOBICO (ISO), si ya la definió. */
  delivery_date?: string | null;
  /** Litros aceptados/entregados que capturó MOBICO. */
  liters?: number | null;
  liters_requested?: number | null;
  truck_capacity?: number | null;
  product?: string | null;
  /** Hay una solicitud de cambio pendiente sobre el pedido. */
  in_flight_change?: boolean;
  /** Cambio en vuelo que el API ya mezcló en la fila (producto, material, capacidad) y cómo estaba antes. */
  pending_change?: {
    previous?: {
      product?: string | null;
      material?: string | null;
      truck_capacity?: number | null;
      liters_requested?: number | null;
    } | null;
  } | null;
  change_kind?: 'addition' | 'cancel' | 'date' | 'capacity' | 'product' | string;
  new_delivery_date?: string | null;
  new_product?: string | null;
  /** La pipa ya llegó (lo calcula el API). */
  delivered?: boolean;
  is_history?: boolean;
  _placeholder?: boolean;
  /** Última modificación del pedido (ISO); con el pedido respondido, es cuándo respondió MOBICO. */
  updated_at?: string | null;
  /** Fecha de creación del pedido (ISO). */
  date?: string | null;
  [extra: string]: any;
}

/** Evento del historial de un pedido (fila hija de la tabla): un cambio resuelto o un ajuste de MOBICO. */
export interface PipesHistoryEvent {
  status?: string;
  /** Fila que nace de un ajuste de MOBICO (no de una solicitud de cambio). */
  is_adjustment?: boolean;
  /** Cuándo quedó el evento como está: respuesta de MOBICO si la hubo, si no cuándo se pidió. */
  _event_ts?: number;
  /** Cuándo se pidió (orden cronológico del historial). */
  _ts?: number;
  po_number?: string | null;
  product?: string | null;
  material?: string | null;
  truck_capacity?: number | null;
  order_delivery_date?: string | null;
  [extra: string]: any;
}

/** Lo que la fila del historial hereda del pedido tal como se pidió originalmente. */
export interface OriginalRequestValues {
  po_number: string | null;
  product: string | null;
  material: string | null;
  truck_capacity: number | null;
  order_delivery_date: string | null;
}

/** Estatus con los que MOBICO ya respondió al pedido: desde ahí `updated_at` es la fecha de la respuesta. */
const ANSWERED_STATUSES = ['confirmed', 'rejected', 'cancelled'];

/** Cuándo respondió MOBICO al pedido (ms); 0 si aún no lo responde. */
export function orderResponseTs(order: PipesOrderRow | null | undefined): number {
  if (!order || !ANSWERED_STATUSES.includes(order.status || '')) {
    return 0;
  }
  return new Date(order.updated_at || order.date || 0).getTime() || 0;
}

/**
 * Último cambio RECHAZADO después de la respuesta de MOBICO al pedido: es lo que la fila del pedido debe mostrar
 * (junta 19-oct: «el último estatus de la solicitud», no «Confirmada» con el rechazo escondido en el historial).
 * Un cambio aprobado no cuenta: ya vive en el pedido y la fila dice «Confirmada» (decisión 16-oct); los ajustes de
 * MOBICO tampoco: se avisan con la marca junto al estatus. Si MOBICO respondió al pedido después del rechazo, gana
 * la respuesta al pedido (null).
 */
export function latestRejectedChange<T extends PipesHistoryEvent>(events: T[] | null | undefined, orderTs: number): T | null {
  let latest: T | null = null;
  (events || []).forEach((e) => {
    if (e.is_adjustment || e.status !== 'rejected') {
      return;
    }
    const ts = e._event_ts ?? e._ts ?? 0;
    if (ts > orderTs && (!latest || ts > (latest._event_ts ?? latest._ts ?? 0))) {
      latest = e;
    }
  });
  return latest;
}

/**
 * El pedido de /purchases con el cambio en vuelo YA mezclado (el Cronograma ve lo pedido aunque MOBICO no lo apruebe):
 * producto, material y capacidad traen los valores NUEVOS y los de antes viajan en `pending_change.previous`. En
 * Solicitudes las columnas llevan lo de ANTES y solo «Nuevo valor» lo pedido (usuario, 19-oct: capacidad pedida de
 * 10,000 a 15,000 se veía 15,000 en las dos columnas), igual que el panel admin y las filas del historial; al
 * aprobarse, el pedido trae lo nuevo y vuelve a leerse como una adición normal. Sin cambio en vuelo devuelve la misma
 * fila. El PO no se toca: el cambio de producto muestra el PO nuevo, como el panel admin.
 */
export function beforeInFlightChange<T extends PipesOrderRow>(row: T): T {
  const before = row?.pending_change?.previous;
  if (!before) {
    return row;
  }
  return {
    ...row,
    product: before.product ?? row.product,
    material: before.material ?? row.material,
    truck_capacity: before.truck_capacity ?? row.truck_capacity,
    liters_requested: before.liters_requested ?? row.liters_requested,
  };
}

/**
 * Valores con los que se PIDIÓ el pedido originalmente, para la línea «Adición» del historial: los de ANTES del
 * evento más antiguo (cada fila del historial muestra la solicitud tal como estaba antes de ese cambio). Sin eventos
 * que lo hayan modificado, los vigentes del pedido.
 */
export function originalRequestValues(order: PipesOrderRow, events: PipesHistoryEvent[] | null | undefined): OriginalRequestValues {
  const oldest = [...(events || [])].sort((a, b) => (a._ts ?? 0) - (b._ts ?? 0))[0];
  const src: PipesHistoryEvent | PipesOrderRow = oldest || order;
  return {
    po_number: src.po_number ?? null,
    product: src.product ?? null,
    material: src.material ?? null,
    truck_capacity: src.truck_capacity ?? null,
    order_delivery_date: src.order_delivery_date ?? null,
  };
}

const numberFormat = new Intl.NumberFormat('es-MX');

/** Pedido ya aprobado que aún admite un cambio: confirmado, sin otra solicitud en curso y sin haber llegado. */
export function canRequestChange(row: PipesOrderRow | null | undefined): boolean {
  return row?.status === 'confirmed' && !row.in_flight_change && !row.delivered && !row.is_history && !row._placeholder;
}

/**
 * Fecha de entrega YA solicitada (YYYY-MM-DD, calendario UTC como las pinta la app). En un cambio de fecha en vuelo es
 * la NUEVA: `order_delivery_date` trae la anterior porque el cambio no toca el pedido hasta que se aprueba.
 */
export function requestedDeliveryDay(row: PipesOrderRow | null | undefined): string {
  const raw = row?.in_flight_change && row.change_kind === 'date' && row.new_delivery_date
    ? row.new_delivery_date
    : row?.order_delivery_date;
  return raw ? moment.utc(raw).format('YYYY-MM-DD') : '';
}

/** Producto YA solicitado: el nuevo si el cambio en vuelo es de producto. */
export function requestedProduct(row: PipesOrderRow | null | undefined): string {
  return row?.in_flight_change && row.change_kind === 'product'
    ? (row.new_product || row.product || '')
    : (row?.product || '');
}

/**
 * Fecha de entrega VIGENTE de la pipa (usuario, 16-oct): la que MOBICO aceptó al aprobar (`delivery_date`, que puede
 * ser otra que la solicitada); si aún no hay, la solicitada. Es la «fecha actual» del modal de cambio, del calendario
 * y del aviso de 48/24 hrs.
 */
export function currentDeliveryDate(row: PipesOrderRow | null | undefined): string | null {
  return row?.delivery_date || row?.order_delivery_date || null;
}

/**
 * Valor de la columna Capacidad: si MOBICO ya definió los litros aceptados/entregados (`liters`) se muestra ese valor,
 * aunque sean menos que los solicitados (usuario, 16-oct); si no, la capacidad solicitada.
 */
export function capacityValue(row: PipesOrderRow | null | undefined): number | null {
  return row?.liters != null ? row.liters : (row?.truck_capacity ?? null);
}

/** Litros solicitados por la estación, con la capacidad de la pipa como respaldo. */
function requestedLiters(row: PipesOrderRow | null | undefined): number | null {
  return row?.liters_requested ?? row?.truck_capacity ?? null;
}

/** Tooltip de la columna Capacidad: con litros aceptados detalla lo solicitado, lo aceptado y la diferencia. */
export function capacityTitle(row: PipesOrderRow | null | undefined): string {
  if (row?.liters == null) {
    return 'Capacidad solicitada';
  }
  const requested = requestedLiters(row);
  if (requested == null) {
    return `Aceptado por MOBICO: ${numberFormat.format(row.liters)} L`;
  }
  const diff = row.liters - requested;
  const diffText = diff === 0 ? 'sin diferencia' : `${diff > 0 ? '+' : '-'}${numberFormat.format(Math.abs(diff))} L`;
  return `Solicitado: ${numberFormat.format(requested)} L\nAceptado: ${numberFormat.format(row.liters)} L\nDiferencia: ${diffText}`;
}

/** Ícono de info en Capacidad: solo cuando el pedido está confirmado y lo aceptado difiere de lo solicitado. */
export function showCapacityDiff(row: PipesOrderRow | null | undefined): boolean {
  const requested = requestedLiters(row);
  return row?.status === 'confirmed' && row?.liters != null && requested != null && row.liters !== requested;
}

/** Mensajes de los candados de anticipación (espejo de NEW_TOO_LATE_MSG / CHANGE_TOO_LATE_MSG del API). */
export const NEW_TOO_LATE_MSG = 'Ya no se puede solicitar para esa fecha: MOBICO recibe solicitudes nuevas hasta 48 horas antes de la entrega (10:00 am de dos días antes).';
export const CHANGE_TOO_LATE_MSG = 'Ya no se puede modificar: MOBICO acepta cambios y cancelaciones hasta 24 horas antes de la entrega (10:00 am del día anterior).';
export const CANCEL_TOO_LATE_MSG = CHANGE_TOO_LATE_MSG;

/**
 * Primer día de entrega (YYYY-MM-DD) que todavía admite la regla de MOBICO (junta 27-oct): la solicitud se acepta hasta
 * las 10:00 am (hora de México) de `daysBefore` días antes de la entrega. 2 = solicitud nueva (48 hrs), 1 = cambio de
 * fecha (24 hrs). Espejo de helpers/change_deadline.js del API, que es quien manda.
 */
export function earliestDeliveryDay(daysBefore: number, now: moment.Moment = moment.tz('America/Mexico_City')): string {
  const mx = now.clone().tz('America/Mexico_City');
  const cutoffToday = mx.clone().startOf('day').add(10, 'hours');
  return mx.clone().startOf('day').add(daysBefore + (mx.isAfter(cutoffToday) ? 1 : 0), 'days').format('YYYY-MM-DD');
}

/**
 * Política de MOBICO (junta 27-oct): cualquier modificación de un pedido —cambio o cancelación— se puede pedir hasta las
 * 10:00 am (hora de México) del día anterior a la entrega vigente (la aceptada por MOBICO o, si no hay, la requerida).
 * Espejo del candado del API (changeAllowed en helpers/change_deadline.js); aquí solo deshabilita los botones con su
 * motivo. La fecha cuenta por su día de calendario (las entregas se guardan a medianoche de México). Sin fecha no se bloquea.
 */
export function cancelWindowOpen(row: PipesOrderRow, now: moment.Moment = moment.tz('America/Mexico_City')): boolean {
  const day = String(row?.delivery_date || row?.order_delivery_date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) { return true; }
  return day >= earliestDeliveryDay(1, now);
}

/** Día (YYYY-MM-DD, calendario UTC) de la columna «Fecha requerida» de Solicitudes: la del pedido. */
export function requiredDay(row: PipesOrderRow | null | undefined): string {
  return row?.order_delivery_date ? moment.utc(row.order_delivery_date).format('YYYY-MM-DD') : '';
}

/**
 * Opciones del filtro «Fecha requerida» de Solicitudes (junta MOBICO + Garbri 27-oct: ubicar las pipas de un día):
 * los días que traen las filas cargadas, en orden, con la etiqueta corta del portal («mié 29 oct»). Primero «Todas».
 */
export function requiredDateOptions(rows: PipesOrderRow[]): { value: string; label: string }[] {
  return dayFilterOptions((rows || []).map(requiredDay));
}

/** Opciones de un filtro por día a partir de días YYYY-MM-DD (únicos, en orden, «mié 29 oct»); primero «Todas». */
export function dayFilterOptions(days: string[]): { value: string; label: string }[] {
  const unique = [...new Set((days || []).filter(Boolean))].sort();
  return [
    { value: '', label: 'Todas las fechas' },
    ...unique.map((day) => ({
      value: day,
      label: new Date(`${day}T12:00:00Z`)
        .toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
        .replace(/\./g, '').replace(',', ''),
    })),
  ];
}

/** Riesgo de capacidad de una pipa (junta MOBICO + Garbri 27-oct): «tight» = cabe, pero la siguiente capacidad ya no;
 *  «over» = ya no cabe. `next` es esa siguiente capacidad (solo en «tight»). */
export interface CapacityRisk {
  level: 'tight' | 'over';
  liters: number;
  space: number;
  next: number | null;
  day: string;
}

/**
 * Lo que mide el aviso de capacidad de una fila del panel admin, o null si no aplica: pipas vivas (pendientes, en
 * revisión o confirmadas sin llegar), sin cancelaciones ni historial. Producto, día y litros son los EFECTIVOS: el nuevo
 * valor si el cambio en vuelo es de producto, fecha o capacidad; si no, lo aceptado por MOBICO o lo solicitado.
 */
export function capacityCheckTarget(row: PipesOrderRow | null | undefined): { product: string; day: string; liters: number } | null {
  if (!row || row.is_history || row._placeholder || row.delivered || row.change_kind === 'cancel') { return null; }
  if (!['pending', 'sent', 'review', 'confirmed'].includes(row.status || '')) { return null; }
  const product = requestedProduct(row);
  const rawDay = row.in_flight_change && row.change_kind === 'date' && row.new_delivery_date
    ? row.new_delivery_date
    : (row.delivery_date || row.order_delivery_date);
  const day = rawDay ? moment.utc(rawDay).format('YYYY-MM-DD') : '';
  const liters = row.in_flight_change && row.change_kind === 'capacity' && row.new_truck_capacity != null
    ? +row.new_truck_capacity
    : (row.liters ?? row.truck_capacity ?? row.liters_requested ?? null);
  return product && day && liters ? { product, day, liters: +liters } : null;
}

/**
 * Riesgo a partir del espacio del tanque ese día (POST /fuel-orders/space-check) y las capacidades de la estación. Una
 * pipa ya aceptada por MOBICO (con litros aceptados, o un cambio sobre un pedido confirmado) ya está dentro del
 * inventario proyectado de ese día, así que su espacio es el libre MÁS sus litros vigentes.
 */
export function capacityRisk(row: PipesOrderRow, space: number | null | undefined, capacities: number[] | null | undefined): CapacityRisk | null {
  const target = capacityCheckTarget(row);
  if (!target || space == null) { return null; }
  const accepted = row.liters != null || (row.in_flight_change && row.order_status === 'confirmed');
  const own = accepted ? +(row.liters ?? row.truck_capacity ?? 0) : 0;
  const room = Math.max(0, space) + own;
  if (target.liters > room) { return { level: 'over', liters: target.liters, space: room, next: null, day: target.day }; }
  const next = nextCapacity(target.liters, capacities);
  return next != null && next > room ? { level: 'tight', liters: target.liters, space: room, next, day: target.day } : null;
}

/** Siguiente capacidad de la lista por encima de esos litros (null si no hay). */
export function nextCapacity(liters: number, capacities: number[] | null | undefined): number | null {
  const bigger = (capacities || []).filter((c) => c > liters).sort((a, b) => a - b);
  return bigger.length ? bigger[0] : null;
}

/** Texto del aviso (tooltip de Capacidad): espacio del tanque ese día y qué ya no cabe. */
export function capacityRiskText(risk: CapacityRisk): string {
  const n = (v: number) => numberFormat.format(Math.round(v));
  const day = moment.utc(risk.day).format('DD/MM/YY');
  return risk.level === 'over'
    ? `Espacio en el tanque el ${day}: ${n(risk.space)} L\nUna pipa de ${n(risk.liters)} L ya no cabe`
    : `Espacio en el tanque el ${day}: ${n(risk.space)} L\nUna pipa de ${n(risk.next as number)} L ya no cabe`;
}
